import React, { useContext, useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  Modal,
  Typography,
} from "@mui/material";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import ArrowBackIosNewOutlinedIcon from "@mui/icons-material/ArrowBackIosNewOutlined";
import { useNavigate } from "react-router-dom";
import { modalBoxStyle } from "../styles";
import Store from "../../../Store/Store";
import useAllServingSizes from "../../../hooks/useAllServingSizes";
import utilFunctions from "../../../utilities/utilFunctions";

// Picks which serving sizes are defaults for a product family. The whole selection saves in one
// call - the API diffs it against what is already attached.
const SelectFamilyServingSizesModal = ({
  isOpen,
  toggleModalOpen,
  productFamily,
  onSave,
}) => {
  const store = useContext(Store);
  const navigate = useNavigate();
  const establishmentId = store.currentEstablishment?.id;

  const [selectedIds, setSelectedIds] = useState([]);
  const [saving, setSaving] = useState(false);

  const { data: servingSizes, isLoading } = useAllServingSizes(
    establishmentId,
    { enabled: isOpen }
  );

  // seed the checkboxes from whatever the family already has each time the modal opens
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setSelectedIds(
      (productFamily?.defaultServingSizes ?? []).map((x) => x.id)
    );
  }, [isOpen, productFamily]);

  const closeModal = () => {
    toggleModalOpen(false);
  };

  const handleToggle = (servingSizeId) => {
    setSelectedIds((current) =>
      current.includes(servingSizeId)
        ? current.filter((x) => x !== servingSizeId)
        : [...current, servingSizeId]
    );
  };

  const handleReset = () => {
    setSelectedIds([]);
  };

  const handleEditServingSizes = () => {
    closeModal();
    navigate(`/spec/${establishmentId}/organization/servingsizes`);
  };

  const handleSave = async () => {
    setSaving(true);
    await onSave(selectedIds);
    setSaving(false);
    closeModal();
  };

  return (
    <Modal
      open={isOpen}
      onClose={closeModal}
      aria-labelledby="family-serving-sizes-title"
      aria-describedby="family-serving-sizes-description"
    >
      <Box sx={modalBoxStyle}>
        {isLoading || saving ? (
          <div style={{ display: "flex", justifyContent: "center" }}>
            <CircularProgress />
          </div>
        ) : (
          <Grid container direction="column">
            <Grid
              container
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Grid item>
                <Button
                  onClick={closeModal}
                  sx={{
                    padding: 0,
                    minWidth: 0,
                    color: (theme) => theme.palette.terrain["01"],
                  }}
                >
                  <ArrowBackIosNewOutlinedIcon
                    sx={{ fontSize: 14, marginRight: 1 }}
                  />
                  <Typography variant="smallButton">Back</Typography>
                </Button>
              </Grid>
              <Grid item>
                <IconButton onClick={closeModal}>
                  <CloseOutlinedIcon />
                </IconButton>
              </Grid>
            </Grid>

            <Grid item sx={{ marginTop: 2 }}>
              <Typography variant="h2" id="family-serving-sizes-title">
                {productFamily?.name}
              </Typography>
            </Grid>
            <Grid item sx={{ marginTop: 2 }}>
              {/* terrain["02"] flips with the mode - the numbered terrain scale inverts, so
                  terrain[500] reads as near-invisible dark grey in dark mode */}
              <Typography
                variant="body1"
                id="family-serving-sizes-description"
                sx={{ color: (theme) => theme.palette.terrain["02"] }}
              >
                Select the different serving sizes you wish to assign to this
                Product Family
              </Typography>
            </Grid>

            <Grid
              container
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginTop: 6,
              }}
            >
              <Button
                onClick={handleEditServingSizes}
                sx={{ padding: 0, minWidth: 0 }}
              >
                <Typography
                  variant="smallButton"
                  sx={{ color: (theme) => theme.palette.primary[800] }}
                >
                  Edit Serving Sizes
                </Typography>
              </Button>
              <Button
                onClick={handleReset}
                disabled={selectedIds.length === 0}
                sx={{ padding: 0, minWidth: 0 }}
              >
                <Typography
                  variant="smallButton"
                  sx={{ color: (theme) => theme.palette.terrain["02"] }}
                >
                  Reset
                </Typography>
              </Button>
            </Grid>

            <Box
              sx={{
                marginTop: 2,
                maxHeight: "340px",
                overflowY: "auto",
                width: "100%",
              }}
            >
              {(servingSizes ?? []).length === 0 ? (
                <Box sx={{ paddingY: 6, textAlign: "center" }}>
                  <Typography variant="body1">
                    You haven&apos;t created any serving sizes yet.
                  </Typography>
                  <Button
                    onClick={handleEditServingSizes}
                    sx={{ marginTop: 2 }}
                  >
                    <Typography
                      variant="smallButton"
                      sx={{ color: (theme) => theme.palette.primary[800] }}
                    >
                      Create Serving Sizes
                    </Typography>
                  </Button>
                </Box>
              ) : (
                (servingSizes ?? []).map((servingSize) => (
                  <Box key={servingSize.id}>
                    <Box
                      onClick={() => handleToggle(servingSize.id)}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        cursor: "pointer",
                        paddingRight: 2,
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Checkbox
                          checked={selectedIds.includes(servingSize.id)}
                          onChange={() => handleToggle(servingSize.id)}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <Typography variant="body1">
                          {servingSize.name}
                        </Typography>
                      </Box>
                      <Typography
                        variant="body1"
                        sx={{ color: (theme) => theme.palette.terrain["02"] }}
                      >
                        {utilFunctions.extractServingSizeDisplay(servingSize)}
                      </Typography>
                    </Box>
                    <Divider />
                  </Box>
                ))
              )}
            </Box>

            <Box sx={{ marginTop: 6, width: "100%" }}>
              <Button
                variant="contained"
                fullWidth
                onClick={handleSave}
                sx={{ height: "40px", borderRadius: 2 }}
              >
                <Typography variant="largeButton">Save Size Presets</Typography>
              </Button>
            </Box>
          </Grid>
        )}
      </Box>
    </Modal>
  );
};

export default SelectFamilyServingSizesModal;
